import React, { useState, useEffect, useContext } from 'react';
import s from './EventFilters.module.scss';
import ButtonPrimary from '../ButtonPrimary/ButtonPrimary';
import { useTranslation } from 'next-i18next';
import { createPortal } from 'react-dom';
import { trackEventCategory } from '../../utils/dataLayer';
import Image from 'next/image';
import classNames from 'classnames';
import { ACTION_TYPES } from '../../containers/EventListPage/EventListPage';
import { EventListPageContext } from '../../containers/EventListPage/EventListPageContext';
import FocusLock from 'react-focus-lock';

const EventFilters = () => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(EventListPageContext);
  const [isOpen, setIsOpen] = useState(false);
  const [pendingClear, setPendingClear] = useState(false);
  const [filters, setFilters] = useState({
    language: [],
    event_types: [],
    free_events_only: false,
    digital_events_only: false,
    impact_days_only: false,
  });
  const [selectedFilters, setSelectedFilters] = useState(0);

  useEffect(() => {
    if (!state) return;

    setFilters({
      language: state.selectedLanguages || [],
      event_types: state.selectedEventTypes || [],
      free_events_only: state.freeEventsOnly || false,
      digital_events_only: state.digitalEventsOnly || false,
      impact_days_only: state.impactDaysOnly || false,
    });

    const count =
      (state.selectedLanguages?.length || 0) +
      (state.selectedEventTypes?.length || 0) +
      (state.freeEventsOnly ? 1 : 0) +
      (state.digitalEventsOnly ? 1 : 0) +
      (state.impactDaysOnly ? 1 : 0);

    setSelectedFilters(count);
  }, [state]);

  const updateFilter = (key, value) => {
    let newFilters = { ...filters };

    if (key === 'language') {
      const languageValue = value.value;
      const isSelected = newFilters.language.includes(languageValue);

      if (isSelected) {
        newFilters.language = newFilters.language.filter(
          (lang) => lang !== languageValue
        );
      } else {
        newFilters.language.push(languageValue);

        if (!state.isInitialLoad) {
          trackEventCategory(`language-${languageValue}`);
        }
      }
    } else if (key === 'event_types') {
      const isSelected = newFilters.event_types.includes(value);

      if (isSelected) {
        newFilters.event_types = newFilters.event_types.filter(
          (type) => type !== value
        );
      } else {
        newFilters.event_types.push(value);

        if (!state.isInitialLoad) {
          trackEventCategory(value);
        }
      }
    } else if (key === 'free_events_only') {
      const isSelected = filters.free_events_only;

      newFilters.free_events_only = !isSelected;

      if (!isSelected && !state.isInitialLoad) {
        trackEventCategory('free_events_only');
      }
    } else if (key === 'digital_events_only') {
      const isSelected = filters.digital_events_only;

      newFilters.digital_events_only = !isSelected;

      if (!isSelected && !state.isInitialLoad) {
        trackEventCategory('digital');
      }
    } else if (key === 'impact_days_only') {
      const isSelected = filters.impact_days_only;

      newFilters.impact_days_only = !isSelected;

      if (!isSelected && !state.isInitialLoad) {
        trackEventCategory('impact_days_only');
      }
    } else {
      newFilters[key] = !filters[key];
    }

    setFilters(newFilters);
    setPendingClear(false);
  };

  const applyFilters = () => {
    if (pendingClear) {
      dispatch({ type: ACTION_TYPES.CLEAR_ALL_FILTERS });
      setPendingClear(false);
    } else {
      dispatch({
        type: ACTION_TYPES.LANGUAGE_SELECTED,
        languages: filters.language,
      });

      dispatch({
        type: ACTION_TYPES.EVENT_TYPES_SELECTED,
        eventTypes: filters.event_types,
      });

      const freeState = Boolean(state.freeEventsOnly);
      const digitalState = Boolean(state.digitalEventsOnly);
      const impactDaysState = Boolean(state.impactDaysOnly);

      if (filters.free_events_only !== freeState) {
        dispatch({ type: ACTION_TYPES.TOGGLE_FREE });
      }

      if (filters.digital_events_only !== digitalState) {
        dispatch({ type: ACTION_TYPES.TOGGLE_DIGITAL });
      }

      if (filters.impact_days_only !== impactDaysState) {
        dispatch({ type: ACTION_TYPES.TOGGLE_IMPACT_DAYS });
      }
    }

    setIsOpen(false);
  };

  const clearFilters = () => {
    setFilters({
      language: [],
      event_types: [],
      free_events_only: false,
      digital_events_only: false,
      impact_days_only: false,
    });

    setPendingClear(true);
  };

  const toggleFilterPicker = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <div className={s.Root}>
      <div className={s.FilterButtonContainer}>
        <ButtonPrimary
          icon="icon-filter"
          size={'xsmall'}
          modifiers={['InlineFlex', 'NoWrap', 'EventCategory']}
          onClick={toggleFilterPicker}>
          {t('EventFilters.filter')}
        </ButtonPrimary>
        {selectedFilters > 0 && (
          <div className={s.SelectedFilters}>{selectedFilters}</div>
        )}
      </div>

      {isOpen &&
        createPortal(
          <div className={s.ModalContainer}>
            <FocusLock>
              <div className={s.FilterContainer}>
                <i className={s.Bg} aria-hidden={true} />
                <TopControls setIsOpen={setIsOpen} t={t} />
                <Filters
                  filters={filters}
                  updateFilter={updateFilter}
                  eventTypes={state.eventTypes}
                  languages={state.languages}
                  t={t}
                />
                <BottomControls
                  onApplyFilters={applyFilters}
                  onClear={clearFilters}
                  t={t}
                />
              </div>
            </FocusLock>
          </div>,
          document.body
        )}
    </div>
  );
};

const TopControls = ({ setIsOpen, t }) => (
  <div className={s.TopControls}>
    <i className={classNames(s.Bg, s.BgTop)} aria-hidden={true} />
    <h3 className={s.TopControlsTitle}>{t('EventFilters.filters')}</h3>
    <button className={s.Close} onClick={() => setIsOpen(false)}>
      <Image width={17} height={17} src={`/img/icon-cross-white.svg`} alt="" />
    </button>
  </div>
);

const Filters = ({ eventTypes, filters, updateFilter, languages = [], t }) => {
  return (
    <div className={s.Filters}>
      <div className={s.Filter}>
        <span className={s.FilterText}>{t('EventFilters.eventType')}</span>
        <div className={s.FilterWrapper}>
          {eventTypes.map((type) => (
            <ButtonPrimary
              key={type.slug}
              size="xsmall"
              onClick={() => updateFilter('event_types', type.slug)}
              isActive={filters.event_types.includes(type.slug)}
              modifiers={['InlineFlex', 'NoWrap', 'EventCategory']}>
              {type.title}
            </ButtonPrimary>
          ))}
        </div>
      </div>

      <div className={s.Filter}>
        <span className={s.FilterText}>{t('EventFilters.eventLanguage')}</span>
        <div className={s.FilterWrapper}>
          {languages.map(({ label, value }) => (
            <ButtonPrimary
              key={value}
              size="xsmall"
              onClick={() => updateFilter('language', { label, value })}
              isActive={filters.language.includes(value)}
              modifiers={['InlineFlex', 'NoWrap', 'EventCategory']}>
              {label}
            </ButtonPrimary>
          ))}
        </div>
      </div>

      <div className={s.Filter}>
        <span className={s.FilterText}>
          {t('EventFilters.eventOtherFilters')}
        </span>
        <div className={s.FilterWrapper}>
          <button
            type="button"
            className={classNames(s.FilterButton, {
              [s.Active]: filters.free_events_only,
            })}
            onClick={() => updateFilter('free_events_only')}>
            <span className={s.FilterButtonIcon}></span>
            <span className={s.FilterButtonText}>{t('EventFilters.free')}</span>
          </button>
          <button
            type="button"
            className={classNames(s.FilterButton, {
              [s.Active]: filters.digital_events_only,
            })}
            onClick={() => updateFilter('digital_events_only')}>
            <span className={s.FilterButtonIcon}></span>
            <span className={s.FilterButtonText}>
              {t('EventFilters.digital')}
            </span>
          </button>
          <button
            type="button"
            className={classNames(s.FilterButton, {
              [s.Active]: filters.impact_days_only,
            })}
            onClick={() => updateFilter('impact_days_only')}>
            <span className={s.FilterButtonIcon}></span>
            <span className={s.FilterButtonText}>
              {t('EventFilters.impactDays')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

const BottomControls = ({ onApplyFilters, onClear, t }) => {
  return (
    <div className={s.BottomControls}>
      <i className={classNames(s.Bg, s.BgBottom)} aria-hidden={true} />
      <button className={s.ClearButton} onClick={onClear}>
        <Image width={24} height={24} src={`/img/icon-trash.svg`} alt="" />
        <span className={s.ClearButtonText}>{t('EventFilters.clear')}</span>
      </button>
      <ButtonPrimary modifiers={['PurpleBlue']} onClick={onApplyFilters}>
        {t('EventFilters.showEvents')}
      </ButtonPrimary>
    </div>
  );
};

export default EventFilters;
